import { useEffect, useState } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import api from '../../services/api';
import { ArrowLeft, FileText, Clock } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { markdownToHtml } from '../../components/RichTextEditor';
import { sanitizeHtml } from '../../utils/sanitize';

const DOCUMENT_TITLES = {
  'privacy-policy': 'Privacy Policy',
  'shipping-policy': 'Shipping Policy',
  'terms-of-service': 'Terms of Service',
  'return-refund-policy': 'Return & Refund Policy',
  'do-not-sell-info': 'Do Not Sell My Personal Information'
};

const DOCUMENT_TYPES = {
  'privacy-policy': 'privacy_policy',
  'shipping-policy': 'shipping_policy',
  'terms-of-service': 'terms_of_service',
  'return-refund-policy': 'return_refund_policy',
  'do-not-sell-info': 'do_not_sell_info'
};

// Get home path based on user role
const getHomePath = (user) => {
  if (!user) return '/';
  const role = user.role?.toLowerCase();
  switch (role) {
    case 'admin':
      return '/admin';
    case 'staff':
      return '/staff';
    case 'coach':
      return '/coach';
    case 'parent':
      return '/parent';
    case 'customer':
      return '/customer/lane-booking';
    case 'player':
      return '/player';
    default:
      return '/';
  }
};

const LegalDocumentView = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const docType = location.pathname.replace('/', ''); // Get path without leading slash
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  
  // Wait a short moment after auth completes to ensure user state is populated
  useEffect(() => {
    if (!authLoading) {
      // Small delay to ensure React state updates are complete
      const timer = setTimeout(() => {
        setAuthReady(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [authLoading]);

  useEffect(() => {
    const fetchDocument = async () => {
      const type = DOCUMENT_TYPES[docType];
      if (!type) {
        setError('Document not found');
        setLoading(false);
        return;
      }

      try {
        const response = await api.get(`/public/documents/${type}`);
        setDocument(response.data);
      } catch (err) {
        if (err.response?.status === 404) {
          setError('This document has not been published yet.');
        } else {
          setError('Failed to load document');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDocument();
  }, [docType]);

  // Wait for auth to be fully ready and document loading complete
  if (loading || authLoading || !authReady) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  if (error) {
    // Use the browser's history to go back if possible, otherwise use role-based navigation
    const handleBack = () => {
      // If user came from within the app, go back
      if (window.history.length > 1) {
        window.history.back();
      } else {
        // Fallback to role-based navigation
        navigate(getHomePath(user));
      }
    };
    
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 lg:p-8 text-center">
            <FileText className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-700 mb-2">
              {DOCUMENT_TITLES[docType] || 'Document'}
            </h2>
            <p className="text-slate-500 mb-6">{error}</p>
            <Button variant="outline" onClick={handleBack} data-testid="back-button">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to={getHomePath(user)} className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
            <ArrowLeft className="h-4 w-4" />
            <span>Back</span>
          </Link>
          {document?.last_updated && (
            <div className="flex items-center gap-1 text-sm text-slate-500">
              <Clock className="h-4 w-4" />
              Last updated: {new Date(document.last_updated).toLocaleDateString()}
            </div>
          )}
        </div>
      </div>

      {/* Document Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Card className="bg-white shadow-sm">
          <CardContent className="p-8 md:p-12">
            <h1 className="text-3xl font-bold text-slate-900 mb-6">
              {document?.title || DOCUMENT_TITLES[docType]}
            </h1>
            
            {document?.version && (
              <p className="text-sm text-slate-500 mb-6">Version {document.version}</p>
            )}

            <div 
              className="prose prose-slate max-w-none prose-headings:text-slate-900 prose-p:text-slate-600 prose-a:text-blue-600"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(document?.content ? markdownToHtml(document.content) : '<p>No content available.</p>') }}
            />
          </CardContent>
        </Card>

        {/* Footer Links */}
        <div className="mt-8 text-center">
          <p className="text-sm text-slate-500 mb-4">Other Legal Documents</p>
          <div className="flex flex-wrap justify-center gap-4">
            {Object.entries(DOCUMENT_TITLES).map(([slug, title]) => (
              slug !== docType && (
                <Link
                  key={slug}
                  to={`/${slug}`}
                  className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
                >
                  {title}
                </Link>
              )
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalDocumentView;
